import uuid from "uuid/v4";
import { formatMessage } from "lib/globalize-util";
import { List, fromJS } from "immutable";

export type IGroupList = List<Map<string, any>>;

export enum GroupType {
    WhiteList = "whitelist",
    BlackList = "blacklist",
}

export const removeTemporaryIds = (groups) => {
    return groups.map((group) => {
        if (group.get("_id").includes("_")) {
            group = group.delete("_id");
        }
        group = group.set(
            "product_filter_values",
            group.get("product_filter_values").map((field) => {
                if (field.get("_id").includes("_")) {
                    field = field.delete("_id");
                }
                return field;
            }),
        );
        return group;
    });
};
export const setDefaultGroupNames = (groups) =>
    groups.map((group) => (group.get("name") === "" ? group.set("name", formatMessage("Untitled Group")) : group));

// State updating functions
export const addGroup =
    (excludeNew: boolean) =>
    ({ groups }: { groups: IGroupList }) => ({
        canSave: true,
        groups: groups.push(
            fromJS({
                _id: `_${uuid()}`.replace(/-/g, ""),
                name: formatMessage("Untitled Group"),
                type: GroupType.WhiteList,
                product_filter_values: [],
                exclude: excludeNew,
            }),
        ),
    });
export const addRule = (groups, groupId, fields) => {
    const groupIndex = groups.findIndex((group: any) => group.get("_id") === groupId);
    const availableFields = fields.filterNot((field) => {
        return groups
            .getIn([groupIndex, "product_filter_values"])
            .find((rule) => rule.get("field") === field.get("key"));
    });
    if (groupIndex === -1) {
        return null;
    }
    return {
        canSave: true,
        groups: groups.updateIn([groupIndex, "product_filter_values"], (rules) =>
            rules.push(
                fromJS({
                    _id: `_${uuid()}`.replace(/-/g, ""),
                    field: availableFields.first().get("key"),
                    values: [],
                }),
            ),
        ),
    };
};
export const removeGroupRuleValue = (groups, groupId, ruleId, fieldValue) => {
    const groupIndex = groups.findIndex((group) => group.get("_id") === groupId);
    const ruleIndex = groups
        .getIn([groupIndex, "product_filter_values"])
        .findIndex((rule) => rule.get("_id") === ruleId);
    const valueIndex = groups
        .getIn([groupIndex, "product_filter_values", ruleIndex, "values"])
        .findIndex((value) => value === fieldValue);
    if (groupIndex === -1 || ruleIndex === -1 || valueIndex === -1) {
        return null;
    }
    return {
        canSave: true,
        groups: groups.deleteIn([groupIndex, "product_filter_values", ruleIndex, "values", valueIndex]),
    };
};
export const removeGroupRule = ({ groups, ruleToRemove }) => {
    const [groupId, ruleId] = ruleToRemove.split("-");
    const groupIndex = groups.findIndex((group) => group.get("_id") === groupId);
    const ruleIndex = groups
        .getIn([groupIndex, "product_filter_values"])
        .findIndex((rule) => rule.get("_id") === ruleId);
    if (groupIndex === -1 || ruleIndex === -1) {
        return null;
    }
    return {
        canSave: true,
        isRemoveRuleDialogOpen: false,
        groups: groups.deleteIn([groupIndex, "product_filter_values", ruleIndex]),
        ruleToRemove: "",
    };
};
export const removeGroup = ({ groups, groupToRemove }) => {
    const groupIndex = groups.findIndex((group) => group.get("_id") === groupToRemove);
    if (groupIndex === -1) {
        return null;
    }
    return {
        canSave: true,
        isRemoveGroupDialogOpen: false,
        groups: groups.delete(groupIndex),
        groupToRemove: "",
    };
};
export const selectGroupRuleField = (groups, groupId, ruleId, fieldKey) => {
    const groupIndex = groups.findIndex((group) => group.get("_id") === groupId);
    const ruleIndex = groups
        .getIn([groupIndex, "product_filter_values"])
        .findIndex((rule) => rule.get("_id") === ruleId);
    if (groupIndex === -1 || ruleIndex === -1) {
        return null;
    }
    return {
        canSave: true,
        groups: groups.updateIn([groupIndex, "product_filter_values", ruleIndex], (rule) =>
            rule.set("field", fieldKey.toLowerCase()),
        ),
    };
};
export const updateGroupField = (groups: IGroupList, groupId: string, key: string, value: string | boolean) => {
    const groupIndex = groups.findIndex((group) => group.get("_id") === groupId);
    if (groupIndex === -1) {
        return null;
    }
    return {
        canSave: true,
        groups: groups.update(groupIndex, (group) => group.set(key, value)),
    };
};
export const toggleGroupType = (groups, groupId) => {
    const groupIndex = groups.findIndex((group) => group.get("_id") === groupId);
    if (groupIndex === -1) {
        return null;
    }
    return {
        canSave: true,
        groups: groups.update(groupIndex, (group) =>
            group.get("type") === GroupType.WhiteList
                ? group.set("type", GroupType.BlackList)
                : group.set("type", GroupType.WhiteList),
        ),
    };
};
export const toggleRuleType = (groups, groupId, ruleId) => {
    const groupIndex = groups.findIndex((group) => group.get("_id") === groupId);
    const ruleIndex = groups
        .getIn([groupIndex, "product_filter_values"])
        .findIndex((rule) => rule.get("_id") === ruleId);
    return {
        canSave: true,
        groups: groups.updateIn([groupIndex, "product_filter_values", ruleIndex], (rule) => {
            rule = rule.has("hide") ? rule.remove("hide") : rule.set("hide", true);
            return rule;
        }),
    };
};

export const checkForRules = (groups) => {
    return groups.filter((group) => group.get("product_filter_values").size).size;
};
